import { makeStyles, Tooltip } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { getMonthEnglish, getPrettyDayTimeUTC } from '@mogul/shared';
import { formatEther } from 'ethers/lib/utils';
import React, { useContext, useEffect, useState } from 'react';

import bnbStakingLogo from '../../common/svg/starsStaking/bnbStaking.svg';
import clock from '../../common/svg/starsStaking/clock.svg';
import starsStakingLogo from '../../common/svg/starsStaking/starsStaking.svg';
import { ModalStateContext } from '../../contexts/app-state-context/modal-state.context';
import { useWeb3State } from '../../hooks';
import { useStarsEthLpTokenContract } from '../../hooks/lp-token-contract.hooks';
import { getNetworkCurrencySymbol } from '../../lib/smart-contract-utils';
import { ConnectWalletModal } from '../common/modal';
import type { StakingModalProps } from '../common/modal/staking/StakeModal.component';
import { createStakeModal } from '../common/modal/staking/StakeModal.component';
import styles from './StakingPool.module.css';

const useStyles = makeStyles(() => ({
  customWidth: {
    maxWidth: 350,
  },
}));

export enum PoolStatus {
  Active = 'Active',
  Upcoming = 'Upcoming',
  Closed = 'Closed',
}

export interface Props {
  poolId: number;
  poolName: string;
  poolDescription: string[];
  duration: number;
  lockup: number;
  apr: string;
  totalDepositedLP: number;
  starsPerBlock: number;
  minLockUpBlocks: number;
  startBlock: number;
  endBlock: number;
  starsToUSDRatio: number;
  bnbToUSDRatio: number;
  blockNumber: number;
}

export const StakingPool: React.FC<Props> = props => {
  const [os, setOs] = useState('WindowsLike');
  const [usdPrice, setUsdPrice] = useState('0.00');
  const [lpTotalSupply, setLpTotalSupply] = useState(0);
  const { setModal } = useContext(ModalStateContext);
  const [endDate, setEndDate] = useState(0);
  const [loading, setLoading] = useState(true);

  const { walletDetails } = useWeb3State();

  const { getStarsEthTotalSupply, getStarsEthReserves, initialized } =
    useStarsEthLpTokenContract();

  const classes = useStyles();

  useEffect(() => {
    if (
      navigator.userAgent.includes('Mac') ||
      navigator.userAgent.includes('like Mac')
    ) {
      setOs('MacintoshLike');
    }
  });

  useEffect(() => {
    if (props.blockNumber !== 0) {
      setLoading(false);
    }
  }, [props.blockNumber]);

  useEffect(() => {
    if (!initialized) {
      return;
    }

    const setLiveValues = async () => {
      try {
        const currentLpTotalSupply = parseFloat(
          formatEther(await getStarsEthTotalSupply()),
        );

        const currentBnbStarsReserves = await getStarsEthReserves();
        const bnbReserves = parseFloat(formatEther(currentBnbStarsReserves[0]));
        const starsReserves = parseFloat(
          formatEther(currentBnbStarsReserves[1]),
        );

        if (props.blockNumber > props.endBlock) {
          const end =
            new Date().getTime() - (props.blockNumber - props.endBlock) * 3000;
          setEndDate(end);
        }

        if (props.poolId === 2 && currentLpTotalSupply !== 0) {
          const lpRatio = props.totalDepositedLP / currentLpTotalSupply;
          const lpToStars = starsReserves * lpRatio;
          const lpToBnb = bnbReserves * lpRatio;
          setUsdPrice(
            `${(
              props.starsToUSDRatio * lpToStars +
              props.bnbToUSDRatio * lpToBnb
            ).toFixed(2)}`,
          );
          setLpTotalSupply(currentLpTotalSupply);
        } else {
          setUsdPrice(
            `${(props.starsToUSDRatio * props.totalDepositedLP).toFixed(2)}`,
          );
        }
        setLoading(false);
      } catch (e) {
        console.error(e);
      }
    };

    void setLiveValues();
  }, [initialized]);

  const handleClickStake = () => {
    if (!walletDetails.address) {
      setModal(ConnectWalletModal());
      return;
    }

    const passedProps: StakingModalProps = {
      poolId: props.poolId,
      poolName: props.poolName,
      poolDuration: props.duration,
      lockupPeriod: props.lockup,
      apr: props.apr,
      lpUSD: parseFloat(usdPrice),
      lpTotalSupply,
    };

    setModal(createStakeModal(passedProps));
  };

  return (
    <>
      {loading && <Skeleton variant="rect" height={436} width={380} />}
      {!loading && (
        <div
          className={
            styles.poolCardContainer +
            ' rounded-full p-3 box-border flex flex-col items-center gap-4'
          }
        >
          <div className={'w-full flex justify-between'}>
            {props.blockNumber > props.endBlock ? (
              <div
                className={
                  styles.closed + ' flex justify-center items-center px-1'
                }
              >
                <div
                  style={{ backgroundColor: '#FFFFFF' }}
                  className={styles.circle}
                />
                {PoolStatus.Closed} Pool
              </div>
            ) : props.blockNumber >= props.startBlock ? (
              <div
                className={
                  styles.active + ' flex justify-center items-center px-1'
                }
              >
                <div
                  style={{ backgroundColor: '#03C04F' }}
                  className={styles.circle}
                ></div>
                {PoolStatus.Active} Pool
              </div>
            ) : (
              <div
                className={
                  styles.upcoming + ' flex justify-center items-center px-1'
                }
              >
                <div
                  style={{ backgroundColor: '#EFB010' }}
                  className={styles.circle}
                />
                {PoolStatus.Upcoming} Pool
              </div>
            )}
            <Tooltip
              title={
                <div>
                  {props.poolDescription.map((entry, i) => {
                    return (
                      <div key={entry + i.toString()}>
                        {entry}
                        <br />
                      </div>
                    );
                  })}
                </div>
              }
            >
              <div
                className={
                  styles.cicleQuestionMark +
                  ' flex justify-center items-center font-black pointer text-lg'
                }
              >
                ?
              </div>
            </Tooltip>
          </div>
          <div
            className={
              styles.poolTitle +
              ' font-bold flex justify-center items-start gap-2 relative text-center'
            }
          >
            {props.poolName === 'The Oscar’s Pool' ? (
              <img
                height="30"
                className={'self-center'}
                src={bnbStakingLogo}
                alt="bnbStakingLogo z-0"
              />
            ) : null}
            <img
              height="30"
              className={
                props.poolName === 'The Oscar’s Pool'
                  ? '-ml-6 z-10 self-center'
                  : 'z-10 self-center'
              }
              src={starsStakingLogo}
              alt="starsStakingLogo"
            />

            <div
              className={
                os === 'WindowsLike' ? 'text-3xl mt-1' : 'text-3xl mb-1'
              }
            >
              {props.poolName}
            </div>
          </div>
          <div
            className={
              styles.totalStars + ' font-bold flex flex-col items-center gap-1'
            }
          >
            <div className="text-center">
              {props.totalDepositedLP.toLocaleString()}{' '}
              {props.poolName === 'The Oscar’s Pool'
                ? `MOGUL-${getNetworkCurrencySymbol()} LP`
                : 'MOGUL'}
            </div>
            <div className={styles.totalSubHeader}>
              TOTAL{' '}
              {props.poolName === 'The Oscar’s Pool'
                ? `MOGUL-${getNetworkCurrencySymbol()} LP`
                : 'MOGUL'}{' '}
              STAKED
            </div>
          </div>
          <hr className={styles.breakline + ' w-11/12'} />
          <div className={'w-11/12 flex justify-between items-center'}>
            <Tooltip
              title="The total rewards earned since the start of staking period."
              placement="top-start"
              classes={{ tooltip: classes.customWidth }}
            >
              <div
                className={
                  styles.statsLabel + ' flex justify-content items-center gap-1'
                }
              >
                Vested Rewards
                <div
                  className={
                    styles.smallerCircle +
                    ' flex justify-center items-center font-black pointer'
                  }
                >
                  ?
                </div>
              </div>
            </Tooltip>
            <div className="flex justify-center items-center gap-1">
              {(
                props.starsPerBlock *
                Math.max(
                  Math.min(props.blockNumber, props.endBlock) -
                    props.startBlock,
                  0,
                )
              ).toFixed(0)}
              <div className={'text-xs mt-1'}>MOGUL</div>
            </div>
          </div>
          <div className={'w-11/12 flex justify-between items-center'}>
            <div
              className={
                styles.statsLabel + ' flex justify-content items-center gap-1'
              }
            >
              APR
              <Tooltip
                title="An annual or annualized return is a measure of how much an investment has increased on average each year."
                placement="top"
                classes={{ tooltip: classes.customWidth }}
              >
                <div
                  className={
                    styles.smallerCircle +
                    ' flex justify-center items-center font-black pointer'
                  }
                >
                  ?
                </div>
              </Tooltip>
            </div>
            <div className="flex justify-center items-end">
              {props.apr === '0.00' || Number.isNaN(props.apr)
                ? 'N/A'
                : '~ ' + props.apr}
              <div className={'ml-1'}>
                {props.apr === '0.00' || Number.isNaN(props.apr) ? '' : '%'}
              </div>
            </div>
          </div>
          <div className={'w-11/12 flex justify-between items-center'}>
            <div
              className={
                styles.statsLabel + ' flex justify-content items-center gap-1'
              }
            >
              TVL
              <Tooltip
                title="Total value locked (TVL) is the overall value of assets deposited in USD."
                placement="top"
                classes={{ tooltip: classes.customWidth }}
              >
                <div
                  className={
                    styles.smallerCircle +
                    ' flex justify-center items-center font-black pointer'
                  }
                >
                  ?
                </div>
              </Tooltip>
            </div>
            <div className="flex justify-content items-end">
              {usdPrice === '0.00' || Number.isNaN(usdPrice)
                ? 'N/A'
                : `$ ${usdPrice}`}
              <div className={'text-sm mt-1 ml-1'}>
                {usdPrice === '0.00' || Number.isNaN(usdPrice) ? '' : 'USD'}
              </div>
            </div>
          </div>
          {props.blockNumber > props.endBlock ? (
            <div
              className={
                styles.poolButtonDisabled +
                ' w-11/12 flex justify-center items-center gap-1 text-xs'
              }
            >
              <img src={clock} width="20px" height="20px" alt="clock" />
              <div>~</div>
              <div>{getMonthEnglish(new Date(endDate).getUTCMonth() + 1)}</div>
              <div>{new Date(endDate).getUTCDate()},</div>
              <div>{new Date(endDate).getUTCFullYear()}</div>
              {getPrettyDayTimeUTC(new Date(endDate))}
            </div>
          ) : (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events
            <div
              role="button"
              tabIndex={0}
              className={
                styles.poolButton +
                ' w-11/12 flex justify-center items-center pointer'
              }
              onClick={handleClickStake}
            >
              STAKE
            </div>
          )}
        </div>
      )}
    </>
  );
};
